// SubmissionDetail — /dashboard/submissions/:id
//
// Full detail view: task config, assignment, officer, all 3 verify
// timestamps + face-verify thumbnail, evidence (photos / files / signature),
// timeline (started → submitted → reviewed), responses sent.
//
// Backend: GET /v1/admin/submissions/:id (PR N).
// Spec: docs/specs/location-tasks.md §11.1.

import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ClipboardList, ChevronLeft, RefreshCw, Fingerprint, ScanFace, MapPin,
  FileText, Image as ImageIcon, Upload, PenLine, Clock, MessageSquare, ServerCrash,
} from "lucide-react";
import PageHeader from "../components/PageHeader.js";
import Skeleton from "../components/Skeleton.js";
import EmptyState from "../components/EmptyState.js";
import {
  getSubmission, createResponse, statusLabel, statusTone, type SubmissionDetail as Detail,
} from "../lib/submissionsApi.js";
import { apiFetch } from "../../lib/api-client.js";
import { formatTaipeiDateTime, formatTaipeiTime } from "../../lib/time.js";

type State =
  | { kind: "loading" }
  | { kind: "ok"; data: Detail }
  | { kind: "error"; message: string };

export default function SubmissionDetail(): React.ReactElement {
  const { id = "" } = useParams<{ id: string }>();
  const [state, setState] = useState<State>({ kind: "loading" });

  const load = useCallback(async () => {
    if (!id) return;
    setState({ kind: "loading" });
    try {
      const d = await getSubmission(id);
      setState({ kind: "ok", data: d });
    } catch (e) {
      setState({ kind: "error", message: (e as Error).message });
    }
  }, [id]);

  useEffect(() => { void load(); }, [load]);

  if (state.kind === "loading") return <div className="space-y-4"><Skeleton className="h-48" /></div>;
  if (state.kind === "error") {
    return (
      <EmptyState
        icon={ServerCrash}
        title="載入失敗"
        description={state.message}
        action={<button onClick={() => void load()} className="btn-primary">重試</button>}
      />
    );
  }

  const { submission: s, task: t, assignment: a, officer: o, responses } = state.data;
  if (!s) return <EmptyState icon={ClipboardList} title="找不到紀錄" />;

  const photoKeys = parseR2Keys(s.photo_r2_keys);
  const fileKeys = parseR2Keys(s.file_r2_keys);

  return (
    <div className="space-y-4">
      <Link to="/dashboard/submissions" className="text-xs text-text-muted hover:text-accent inline-flex items-center gap-1 mb-2">
        <ChevronLeft className="h-3.5 w-3.5" /> 返回列表
      </Link>
      <PageHeader
        title={t?.name ?? s.task_id}
        description={`${o?.display_name ?? s.officer_id.slice(0, 10)} · ${a?.location_label ?? s.assignment_id.slice(0, 10)}`}
        actions={
          <button onClick={() => void load()} className="btn-secondary inline-flex items-center gap-1">
            <RefreshCw className="h-4 w-4" /> 重新整理
          </button>
        }
      />

      <div className="grid md:grid-cols-3 gap-3">
        <Stat label="狀態" value={<Pill tone={statusTone(s.status)}>{statusLabel(s.status)}</Pill>} />
        <Stat label="開始時間" value={<span className="font-mono text-xs">{formatTaipeiDateTime(s.started_at)}</span>} />
        <Stat label="提交時間" value={<span className="font-mono text-xs">{formatTaipeiDateTime(s.submitted_at)}</span>} />
      </div>

      {s.skip_reason && (
        <section className="card p-4 bg-warning/10 border-warning/30">
          <h3 className="text-sm font-semibold mb-1">跳過原因</h3>
          <p className="text-xs whitespace-pre-wrap">{s.skip_reason}</p>
        </section>
      )}

      <section className="card p-4 space-y-3">
        <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
          <Clock className="h-4 w-4 text-accent" /> 身分驗證
        </h3>
        {/* scan-wizard-v2 (2026-05+): the verify_* fields shown below
         *  come from scan_session-level verification (one identity check
         *  per scan, OR-aggregated across all matched tasks at that
         *  location), NOT per-submission verification. The audit trail
         *  preserves the same timestamps but the UX is a single
         *  identity step before evidence collection. See
         *  docs/specs/scan-wizard-v2.md §4.2. */}
        <div className="grid md:grid-cols-3 gap-3">
          <VerifyCard
            icon={<Fingerprint className="h-4 w-4" />}
            label="裝置"
            required={t?.verify_device === 1}
            done={!!s.verify_device_assertion_at}
            extra={s.verify_device_assertion_at ? formatTaipeiTime(s.verify_device_assertion_at) : null}
          >
            {s.verify_device_credential_id && (
              <p className="text-2xs text-text-muted font-mono break-all">cred {s.verify_device_credential_id.slice(0, 16)}…</p>
            )}
          </VerifyCard>
          <VerifyCard
            icon={<ScanFace className="h-4 w-4" />}
            label="人臉"
            required={t?.verify_face === 1}
            done={!!s.verify_face_liveness_at}
            extra={s.verify_face_match_score != null ? `${(s.verify_face_match_score * 100).toFixed(0)}/100` : null}
          >
            {s.verify_face_liveness_action && <p className="text-2xs text-text-muted">挑戰：{s.verify_face_liveness_action}</p>}
            {s.verify_face_photo_r2_key && <EvidenceImg r2Key={s.verify_face_photo_r2_key} className="mt-1.5 rounded-md max-w-[160px]" />}
          </VerifyCard>
          {/* 位置驗證 card 暫時隱藏 — NTAG424 已綁實體位置，geo 驗證冗餘。
              既有 submission 的 verify_geo_* 欄位仍在 DB，之後 uncomment 即可看到。 */}
          {/*
          <VerifyCard
            icon={<MapPin className="h-4 w-4" />}
            label="位置"
            required={t?.verify_geo === 1}
            done={!!s.verify_geo_at}
            extra={s.verify_geo_distance_m != null ? `${s.verify_geo_distance_m.toFixed(1)} m` : null}
          >
            {s.verify_geo_lat != null && s.verify_geo_lon != null && (
              <p className="text-2xs text-text-muted font-mono">{s.verify_geo_lat.toFixed(5)}, {s.verify_geo_lon.toFixed(5)}</p>
            )}
          </VerifyCard>
          */}
        </div>
      </section>

      <section className="card p-4 space-y-3">
        <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
          <FileText className="h-4 w-4 text-accent" /> Step 2 證據
        </h3>
        {s.text_response && (
          <div>
            <p className="text-2xs uppercase text-text-muted mb-1">文字回應</p>
            <p className="text-sm whitespace-pre-wrap rounded-md bg-bg-raised p-3">{s.text_response}</p>
          </div>
        )}
        {photoKeys.length > 0 && (
          <div>
            <p className="text-2xs uppercase text-text-muted mb-1.5 inline-flex items-center gap-1"><ImageIcon className="h-3 w-3" />照片 ({photoKeys.length})</p>
            <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
              {photoKeys.map((k) => <EvidenceImg key={k} r2Key={k} className="rounded-md aspect-square object-cover" />)}
            </div>
          </div>
        )}
        {fileKeys.length > 0 && (
          <div>
            <p className="text-2xs uppercase text-text-muted mb-1.5 inline-flex items-center gap-1"><Upload className="h-3 w-3" />檔案 ({fileKeys.length})</p>
            <ul className="space-y-1.5">
              {fileKeys.map((k) => <EvidenceFileLink key={k} r2Key={k} />)}
            </ul>
          </div>
        )}
        {!s.text_response && photoKeys.length === 0 && fileKeys.length === 0 && (
          <p className="text-xs text-text-muted">未提供 Step 2 證據</p>
        )}
      </section>

      <section className="card p-4 space-y-3">
        <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
          <PenLine className="h-4 w-4 text-accent" /> Step 3 簽名
        </h3>
        {s.signature_r2_key
          ? <EvidenceImg r2Key={s.signature_r2_key} className="rounded-md bg-bg-raised max-w-md w-full" />
          : <p className="text-xs text-text-muted">未簽名</p>}
      </section>

      <section className="card p-4 space-y-3">
        <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
          <MessageSquare className="h-4 w-4 text-accent" /> 管理員回覆 ({responses.length})
        </h3>
        {responses.length > 0 && (
          <ul className="space-y-2">
            {responses.map((r) => (
              <li key={r.id} className="rounded-md bg-bg-raised p-3 text-xs">
                <p className="text-text-muted text-2xs mb-1">
                  {formatTaipeiDateTime(r.created_at)}
                  {r.sent_to_line_at ? <span className="ml-2 text-success">· 已推 LINE</span> : null}
                </p>
                <p className="whitespace-pre-wrap">{r.body}</p>
              </li>
            ))}
          </ul>
        )}
        <ReplyForm submissionId={s.id} onSent={() => void load()} />
      </section>

      <details className="card p-3 text-xs text-text-muted">
        <summary className="cursor-pointer">技術詳情</summary>
        <dl className="mt-2 space-y-1 font-mono">
          <Row k="submission_id" v={s.id} />
          <Row k="task_id" v={s.task_id} />
          <Row k="assignment_id" v={s.assignment_id} />
          <Row k="patrol_event_id" v={s.patrol_event_id ?? "—"} />
          <Row k="patrol_session_id" v={s.patrol_session_id ?? "—"} />
          <Row k="user_agent" v={s.user_agent ?? "—"} />
          <Row k="ip_hint" v={s.ip_hint ?? "—"} />
        </dl>
      </details>
    </div>
  );
}

function VerifyCard({ icon, label, required, done, extra, children }: {
  icon: React.ReactNode; label: string; required: boolean; done: boolean; extra?: string | null; children?: React.ReactNode;
}): React.ReactElement {
  const tone = !required ? "bg-bg-raised/50 text-text-muted" : done ? "bg-success/10 border-success/30" : "bg-warning/10 border-warning/30";
  return (
    <div className={`rounded-lg border p-3 ${tone}`}>
      <p className="text-2xs uppercase tracking-wider inline-flex items-center gap-1">{icon}{label}</p>
      <p className="text-sm font-semibold mt-1">{!required ? "未要求" : done ? "完成" : "未完成"}</p>
      {extra && <p className="text-2xs mt-0.5">{extra}</p>}
      {children}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }): React.ReactElement {
  return (
    <div className="rounded-lg bg-bg-raised p-3">
      <p className="text-2xs uppercase text-text-muted">{label}</p>
      <div className="mt-1">{value}</div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }): React.ReactElement {
  return (
    <div className="flex gap-2"><dt className="text-text-secondary w-32">{k}</dt><dd className="break-all">{v}</dd></div>
  );
}

function Pill({ tone, children }: { tone: "default" | "success" | "warning" | "danger" | "info"; children: React.ReactNode }): React.ReactElement {
  const cls =
    tone === "success" ? "bg-success/15 text-success" :
    tone === "warning" ? "bg-warning/15 text-warning" :
    tone === "danger"  ? "bg-danger/15 text-danger" :
    tone === "info"    ? "bg-accent/15 text-accent" :
                         "bg-bg-raised text-text-secondary";
  return <span className={`inline-block text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${cls}`}>{children}</span>;
}

function parseR2Keys(s: string | null): string[] {
  if (!s) return [];
  try { const v = JSON.parse(s); return Array.isArray(v) ? v.filter((x) => typeof x === "string") : []; }
  catch { return []; }
}

function EvidenceImg({ r2Key, className = "" }: { r2Key: string; className?: string }): React.ReactElement {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let revoke: string | null = null;
    void apiFetch(`/v1/patrols/evidence/${r2Key}`)
      .then(async (r) => r.ok ? r.blob() : null)
      .then((blob) => {
        if (!blob) return;
        revoke = URL.createObjectURL(blob);
        setSrc(revoke);
      });
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [r2Key]);
  if (!src) return <div className={`animate-pulse bg-bg-hover ${className}`} />;
  return <img src={src} alt="" className={className} />;
}

function ReplyForm({ submissionId, onSent }: { submissionId: string; onSent: () => void }): React.ReactElement {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const submit = async (): Promise<void> => {
    if (body.trim().length === 0) { setError("請輸入回覆內容"); return; }
    setBusy(true); setError(null); setOkMsg(null);
    try {
      const r = await createResponse(submissionId, body.trim());
      setOkMsg(r.sent_to_line_at ? "已送出並推送至 LINE" : "已送出（LINE 推送失敗，稍後重試）");
      setBody("");
      onSent();
    } catch (e) {
      setError((e as Error).message);
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2 pt-1">
      <p className="text-2xs uppercase tracking-wider text-text-muted">新增回覆</p>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value.slice(0, 2000))}
        rows={3}
        placeholder="輸入回覆內容（會推送至巡邏員的 LINE 收件匣）…"
        className="w-full rounded-md bg-bg-raised border border-border p-2.5 text-xs"
      />
      <div className="flex items-center justify-between">
        <span className="text-2xs text-text-muted">{body.length} / 2000</span>
        <button onClick={() => void submit()} disabled={busy || body.trim().length === 0} className="btn-primary text-xs">
          {busy ? "送出中…" : "送出回覆 + LINE 推送"}
        </button>
      </div>
      {error && <p className="text-xs text-danger">{error}</p>}
      {okMsg && <p className="text-xs text-success">{okMsg}</p>}
    </div>
  );
}

function EvidenceFileLink({ r2Key }: { r2Key: string }): React.ReactElement {
  const filename = r2Key.split("/").pop() ?? r2Key;
  const onClick = async (): Promise<void> => {
    const r = await apiFetch(`/v1/patrols/evidence/${r2Key}`);
    if (!r.ok) return;
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <li className="flex items-center gap-2 rounded-md bg-bg-raised px-3 py-2 text-xs">
      <Upload className="h-3.5 w-3.5 text-text-secondary" />
      <button onClick={() => void onClick()} className="font-mono truncate hover:text-accent text-left flex-1">
        {filename}
      </button>
    </li>
  );
}
